import {
  collection,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import { db, appId } from '../../../services/firebase';

/**
 * Obtener plantillas del usuario desde Firestore
 */
export const getTemplatesForUser = async (userId) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);
    const snapshot = await getDoc(userRef);

    if (snapshot.exists() && snapshot.data().templates) {
      return snapshot.data().templates;
    }
    return null;
  } catch (error) {
    console.error('Error al obtener plantillas:', error);
    return null;
  }
};

/**
 * Guardar plantillas del usuario en Firestore
 */
export const saveTemplatesForUser = async (userId, templates) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);

    await setDoc(
      userRef,
      {
        templates: templates,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error al guardar plantillas:', error);
    throw error;
  }
};

/**
 * Obtener mensajes guardados del usuario
 */
export const getSavedMessagesForUser = async (userId) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);
    const snapshot = await getDoc(userRef);

    if (snapshot.exists() && snapshot.data().savedMessages) {
      return snapshot.data().savedMessages;
    }
    return {};
  } catch (error) {
    console.error('Error al obtener mensajes guardados:', error);
    return {};
  }
};

/**
 * Guardar mensajes generados del usuario
 */
export const saveMessagesForUser = async (userId, messages) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);

    await setDoc(
      userRef,
      {
        savedMessages: messages,
        messagesUpdatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error al guardar mensajes:', error);
    throw error;
  }
};

/**
 * Obtener datos de la masterclass del usuario
 */
export const getMasterclassDataForUser = async (userId) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);
    const snapshot = await getDoc(userRef);

    if (snapshot.exists() && snapshot.data().masterclassData) {
      return snapshot.data().masterclassData;
    }
    return null;
  } catch (error) {
    console.error('Error al obtener datos de masterclass:', error);
    return null;
  }
};

/**
 * Guardar datos de la masterclass del usuario
 */
export const saveMasterclassDataForUser = async (userId, masterclassData) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);

    await setDoc(
      userRef,
      {
        masterclassData: masterclassData,
        masterclassUpdatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error al guardar datos de masterclass:', error);
    throw error;
  }
};

/**
 * Obtener datos de venta del usuario
 */
export const getSalesDataForUser = async (userId) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);
    const snapshot = await getDoc(userRef);

    if (snapshot.exists() && snapshot.data().salesData) {
      return snapshot.data().salesData;
    }
    return null;
  } catch (error) {
    console.error('Error al obtener datos de venta:', error);
    return null;
  }
};

/**
 * Guardar datos de venta del usuario
 */
export const saveSalesDataForUser = async (userId, salesData) => {
  try {
    const userRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config`);

    await setDoc(
      userRef,
      {
        salesData: salesData,
        salesUpdatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error al guardar datos de venta:', error);
    throw error;
  }
};

/**
 * Obtener todas las imágenes del usuario desde Firestore
 */
export const getAllImagesForUser = async (userId) => {
  try {
    const imagesRef = collection(db, `artifacts/${appId}/users/${userId}/messageGenerator/config/imagenes`);
    const snapshot = await getDocs(imagesRef);

    if (snapshot.empty) {
      return [];
    }

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Error al obtener imágenes:', error);
    return [];
  }
};

/**
 * Crear o actualizar configuración de una imagen
 */
export const saveImageConfig = async (userId, imageId, imageData) => {
  try {
    const imageRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config/imagenes/${imageId}`);

    await setDoc(
      imageRef,
      {
        ...imageData,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error al guardar configuración de imagen:', error);
    throw error;
  }
};

/**
 * Obtener configuración de una imagen específica
 */
export const getImageConfig = async (userId, imageId) => {
  try {
    const imageRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config/imagenes/${imageId}`);
    const snapshot = await getDoc(imageRef);

    if (snapshot.exists()) {
      return {
        id: snapshot.id,
        ...snapshot.data()
      };
    }
    return null;
  } catch (error) {
    console.error('Error al obtener configuración de imagen:', error);
    return null;
  }
};

/**
 * Actualizar zonas de una imagen
 */
export const updateImageZones = async (userId, imageId, zonas) => {
  try {
    const imageRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config/imagenes/${imageId}`);

    await updateDoc(imageRef, {
      zonas: zonas,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error al actualizar zonas de imagen:', error);
    throw error;
  }
};

/**
 * Eliminar una imagen
 */
export const deleteImage = async (userId, imageId) => {
  try {
    const imageRef = doc(db, `artifacts/${appId}/users/${userId}/messageGenerator/config/imagenes/${imageId}`);
    const imageDoc = await getDoc(imageRef);

    if (imageDoc.exists()) {
      // Si existe un archivo en Storage, eliminarlo será responsabilidad del componente
      // Aquí solo eliminamos el documento de Firestore
      await updateDoc(imageRef, {
        deletedAt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error al eliminar imagen:', error);
    throw error;
  }
};
